import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Mail,
  Settings,
  MessageSquare,
  LogOut,
  Sun,
  ChevronRight,
  Radio,
  Loader2,
  Sparkles,
  ListChecks,

  CalendarDays,
  Calculator,
  Wrench,
} from "lucide-react";
import { NavGroup, NavItem } from "./SidebarNav";
import { useAuth } from "../lib/auth";
import { useLive } from "../lib/live";
import NoreaLogo from "./NoreaLogo";
import AssistantDrawer from "./AssistantDrawer";
import Breadcrumb from "./Breadcrumb";
import NotificationBell from "./NotificationBell";
import { useStatsAccess } from "./StatsAccess";
import { CompanySwitcher } from "./CompanySwitcher";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Vue d'ensemble", testid: "nav-overview" },
  { to: "/dossiers", icon: Inbox, label: "Dossiers", testid: "nav-dossiers" },
  { to: "/reponses", icon: Mail, label: "Mails", testid: "nav-reponses" },
  { to: "/tickets-internes", icon: ListChecks, label: "Tickets internes", testid: "nav-internal-tickets" },
  { to: "/planning", icon: CalendarDays, label: "Planning", testid: "nav-planning" },
  { to: "/comptabilite", icon: Calculator, label: "Comptabilité", testid: "nav-comptabilite" },
  { label: "Service Après Vente", icon: Wrench, testid: "nav-group-sav", children: [
    { to: "/sav", label: "Demandes SAV", testid: "nav-sav-demandes", end: true },
    { to: "/sav/litiges", label: "Litiges", testid: "nav-sav-litiges" },
  ] },
  { label: "Paramètres", icon: Settings, testid: "nav-group-parametres", children: [
    { to: "/configuration", label: "Configuration", testid: "nav-configuration" },
    { to: "/statistiques", label: "Statistiques", testid: "nav-stats", statsOnly: true },
    { to: "/connexions", label: "Connexions", testid: "nav-connections" },
    { to: "/backup-chelly", label: "Backup Chelly", testid: "nav-chelly-backup" },
    { to: "/utilisateurs", label: "Utilisateurs", testid: "nav-users", adminOnly: true },
  ] },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { connected, processing } = useLive();
  const liveCount = processing.length;
  const [assistantOpen, setAssistantOpen] = useState(false);
  const statsAccess = useStatsAccess();

  // Keyboard shortcut: Cmd/Ctrl+K toggles assistant
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAssistantOpen((o) => !o);
      } else if (e.key === "Escape" && assistantOpen) {
        setAssistantOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assistantOpen]);

  return (
    <div className="min-h-screen flex bg-norea-bg">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-norea-border flex flex-col" data-testid="sidebar">
        <div className="px-6 py-5 border-b border-norea-border">
          <div className="flex items-center gap-3">
            <img
              src="/norea-logo-icon.png"
              alt="Norea"
              className="h-9 w-auto select-none"
              draggable={false}
              data-testid="sidebar-logo"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-norea-textMuted">AI Platform</div>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                connected ? "text-emerald-700 bg-emerald-50" : "text-norea-textMuted bg-norea-surfaceHover"
              }`}
              title={connected ? "Connexion temps réel active" : "Connexion temps réel coupée"}
              data-testid="sse-status"
            >
              <Radio className={`w-2.5 h-2.5 ${connected ? "animate-pulse" : ""}`} />
              {connected ? "LIVE" : "OFF"}
            </span>
          </div>
          <CompanySwitcher />
        </div>

        <div className="px-3 py-4 flex-1">
          <div className="overline px-3 mb-2">Pilotage</div>
          <nav className="space-y-0.5">
            {navItems.map((item) => item.children
              ? <NavGroup key={item.testid} group={item} isAdmin={user?.role === "admin"} canStats={!!statsAccess?.allowed} />
              : <NavItem key={item.to} item={item} />)}
          </nav>

          {/* Norea Assistant launcher */}
          <button
            onClick={() => setAssistantOpen(true)}
            className="mt-6 w-full px-3 py-3 rounded-md border border-norea-primary/30 bg-gradient-to-br from-norea-primary/5 to-norea-accent/5 hover:from-norea-primary/10 hover:to-norea-accent/10 transition text-left group"
            data-testid="btn-open-assistant"
            title="Cmd/Ctrl + K"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-norea-primary/15 flex items-center justify-center group-hover:bg-norea-primary/25 transition">
                <Sparkles className="w-3.5 h-3.5 text-norea-primary" />
              </div>
              <span className="text-xs font-semibold text-norea-text">Norea Assistant</span>
              <kbd className="ml-auto text-[9px] font-mono text-norea-textMuted bg-white border border-norea-border rounded px-1 py-0.5">⌘K</kbd>
            </div>
            <div className="mt-1.5 text-[11px] text-norea-textMuted leading-snug">
              Pose une question, lance une relance, fais une synthèse…
            </div>
          </button>

          {/* Support — separated from the operational nav above */}
          <div className="overline px-3 mt-6 mb-2">Support</div>
          <nav className="space-y-0.5">
            <NavLink
              to="/tickets"
              data-testid="nav-tickets"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-norea-surfaceHover text-norea-primary font-medium"
                    : "text-norea-textMuted hover:bg-norea-surfaceHover hover:text-norea-text"
                }`
              }
            >
              <MessageSquare className="w-4 h-4" />
              <span>Tickets d'assistance</span>
            </NavLink>
          </nav>

          {liveCount > 0 && (
            <div
              className="mt-3 px-3 py-3 bg-norea-primary/5 rounded-md border border-norea-primary/30 animate-fade-in"
              data-testid="live-processing-banner"
            >
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-3.5 h-3.5 text-norea-primary animate-spin" />
                <span className="text-xs font-semibold text-norea-primary">
                  {liveCount} dossier{liveCount > 1 ? "s" : ""} en cours
                </span>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {processing.slice(0, 4).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => (window.location.href = `/dossiers/${p.id}`)}
                    className="w-full text-left text-[11px] text-norea-textMuted hover:text-norea-text truncate"
                    data-testid={`live-item-${p.id}`}
                  >
                    → {p.partenaire || "Manuel"}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-norea-border p-3">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-7 h-7 rounded-full bg-norea-accent text-white flex items-center justify-center text-xs font-semibold">
              {(user?.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-norea-text truncate">{user?.email || "—"}</div>
              <div className="text-[10px] text-norea-textMuted">{user?.role || ""}</div>
            </div>
            <NotificationBell />
            <button
              onClick={logout}
              data-testid="btn-logout"
              className="p-1.5 text-norea-textMuted hover:text-norea-text hover:bg-norea-surfaceHover rounded transition"
              title="Se déconnecter"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className="px-8 py-6 max-w-[1400px] mx-auto">{children}</div>
      </main>

      {/* Norea Assistant drawer */}
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        {breadcrumb && <Breadcrumb items={breadcrumb} />}
        <h1 className="font-heading text-3xl font-semibold text-norea-text tracking-tight">{title}</h1>
        {subtitle && <div className="text-sm text-norea-textMuted mt-1">{subtitle}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
